import { useCallback, useEffect, useState } from "react";

import { styled } from "@linaria/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CopyIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@phosphor-icons/react";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import Chip, { ChipSize, ChipVariant } from "@gx/dls/chips/Chip";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import CodeEditor from "@gx/dls/editor/CodeEditor";
import CopyInput from "@gx/dls/inputs/CopyInput";
import Text, { TextVariant, TextWeight } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import Widget from "@gx/dls/widget/Widget";

import { DEFAULT_SIGNATURE_HEADER } from "@/pages/sources/components/create/sources/webhook/constants";
import { type WebhookCredentials } from "@/pages/sources/components/create/sources/webhook/types";
import {
  computeHmacSignature,
  generateCurlCommand,
} from "@/pages/sources/components/create/sources/webhook/utils";

// ─── Styled primitives ─────────────────────────────────────────────────────────

type StepStatus = "active" | "done" | "pending";

const StepCircle = withTheme(styled.div<PropsWithTheme & { $status: StepStatus }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ theme, $status }) => {
    if ($status === "active") return theme.color.background.primaryAlt;
    if ($status === "done") return theme.color.background.success;
    return "transparent";
  }};
  border: 0.5px solid
    ${({ theme, $status }) => ($status === "pending" ? theme.color.border.primary : "transparent")};
`);

const StepConnector = withTheme(styled.div<PropsWithTheme>`
  flex: 1;
  height: 0.5px;
  background: ${({ theme }) => theme.color.border.primary};
  margin-bottom: 14px;
`);

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;

  &:last-child {
    flex: 0;
  }
`;

const InfoRowWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px;
  border-bottom: 0.5px solid ${({ theme }) => theme.color.border.primary};

  &:last-child {
    border-bottom: none;
  }
`);

// ─── InfoRow ──────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <InfoRowWrapper>
    <Text variant={TextVariant.TERTIARY}>{label}</Text>
    <Text variant={TextVariant.SECONDARY}>{value}</Text>
  </InfoRowWrapper>
);

// ─── StepIndicator ────────────────────────────────────────────────────────────

const STEP_LABELS = ["Payload", "Sign", "Submit"] as const;

const StepIndicator = ({ currentStep }: { currentStep: number }) => {
  const getStatus = (index: number): StepStatus => {
    if (index < currentStep) return "done";
    if (index === currentStep) return "active";
    return "pending";
  };

  return (
    <FlexWrapper alignItems={AlignItems.CENTER} fillWidth>
      {STEP_LABELS.map((label, i) => (
        <StepItem key={label}>
          <FlexWrapper direction={FlexDirection.COLUMN} alignItems={AlignItems.CENTER} gap={4}>
            <StepCircle $status={getStatus(i)}>
              {getStatus(i) === "done" ? (
                <CheckIcon size={12} color="white" weight="bold" />
              ) : (
                <Text
                  variant={
                    getStatus(i) === "active" ? TextVariant.PRIMARY_ALT : TextVariant.TERTIARY
                  }
                >
                  {i + 1}
                </Text>
              )}
            </StepCircle>
            <Text variant={getStatus(i) === "active" ? TextVariant.PRIMARY : TextVariant.TERTIARY}>
              {label}
            </Text>
          </FlexWrapper>
          {i < STEP_LABELS.length - 1 && <StepConnector />}
        </StepItem>
      ))}
    </FlexWrapper>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

interface WebhookStepByStepProps {
  credentials: WebhookCredentials;
  signatureHeader?: string;
}

const DEFAULT_PAYLOAD = `{
  "test": "data"
}`;

const WebhookStepByStep = ({
  credentials,
  signatureHeader = DEFAULT_SIGNATURE_HEADER,
}: WebhookStepByStepProps) => {
  const [step, setStep] = useState(0);
  const [payload, setPayload] = useState(DEFAULT_PAYLOAD);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const [signature, setSignature] = useState("");
  const [isComputingSignature, setIsComputingSignature] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  // Compute signature whenever we enter phase 1
  useEffect(() => {
    if (step !== 1) return;
    setIsComputingSignature(true);
    void computeHmacSignature(credentials.webhookSecret, payload.trim())
      .then(setSignature)
      .finally(() => setIsComputingSignature(false));
  }, [step, credentials.webhookSecret, payload]);

  const handleNextFromPayload = useCallback(() => {
    try {
      JSON.parse(payload);
      setPayloadError(null);
      setStep(1);
    } catch {
      setPayloadError("Payload must be valid JSON");
    }
  }, [payload]);

  const testUrl = credentials.webhookUrl.replace("/v1/", "/v1/test/");

  const curlCommand = generateCurlCommand(
    testUrl,
    signatureHeader,
    signature || undefined,
    payload,
  );

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(curlCommand).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [curlCommand]);

  const maskedKey = credentials.webhookSecret.slice(0, 8) + "••••••••••••••••••••";

  return (
    <Widget fillWidth noHover>
      <FlexWrapper direction={FlexDirection.COLUMN} gap={16} fillWidth>
        <StepIndicator currentStep={step} />

        {/* Step 0: Payload */}
        {step === 0 && (
          <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
            <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
              <Text weight={TextWeight.MEDIUM}>Build your payload</Text>
              <Text variant={TextVariant.TERTIARY}>
                Edit the JSON below to match what your service will send.
              </Text>
            </FlexWrapper>
            <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
              <CodeEditor
                content={payload}
                lang="json"
                noLineNumbers
                borderRadius={8}
                onChange={setPayload}
              />
              {payloadError && <Text variant={TextVariant.ERROR}>{payloadError}</Text>}
            </FlexWrapper>
            <FlexWrapper justifyContent={JustifyContent.END} fillWidth>
              <Button
                label="Next"
                icon={ArrowRightIcon}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.SECONDARY}
                onClick={handleNextFromPayload}
                isIconTrailing
              />
            </FlexWrapper>
          </FlexWrapper>
        )}

        {/* Step 1: Sign */}
        {step === 1 && (
          <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
            <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
              <Text weight={TextWeight.MEDIUM}>Sign your payload</Text>
              <Text variant={TextVariant.TERTIARY}>
                Each request must include an HMAC-SHA256 signature so the endpoint can verify it
                came from you. In your application:
              </Text>
            </FlexWrapper>

            <FlexWrapper direction={FlexDirection.COLUMN} gap={0} fillWidth>
              <InfoRow label="1. Serialize" value="Stringify the request body as UTF-8" />
              <InfoRow label="2. Sign" value="HMAC-SHA256(secret, body) → hex digest" />
              <InfoRow label="3. Attach" value={`Set result as the ${signatureHeader} header`} />
            </FlexWrapper>

            <FlexWrapper alignItems={AlignItems.END} gap={8} fillWidth>
              <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
                <FlexWrapper
                  alignItems={AlignItems.CENTER}
                  justifyContent={JustifyContent.SPACE_BETWEEN}
                  fillWidth
                >
                  <Text variant={TextVariant.SECONDARY}>Key</Text>
                  <Chip
                    label="Shown once — save this now"
                    variant={ChipVariant.ERROR}
                    size={ChipSize.SMALL}
                  />
                </FlexWrapper>
                <CodeEditor
                  content={showKey ? credentials.webhookSecret : maskedKey}
                  isReadOnly
                  noLineNumbers
                  borderRadius={8}
                />
              </FlexWrapper>
              <Button
                icon={showKey ? EyeSlashIcon : EyeIcon}
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.MEDIUM}
                onClick={() => setShowKey((v) => !v)}
              />
              <Button
                icon={CopyIcon}
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.MEDIUM}
                onClick={() => void navigator.clipboard.writeText(credentials.webhookSecret)}
              />
            </FlexWrapper>

            <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
              <Text variant={TextVariant.SECONDARY}>Computed Signature</Text>
              {isComputingSignature ? (
                <Text variant={TextVariant.TERTIARY}>Computing...</Text>
              ) : (
                <CopyInput value={signature} fillWidth />
              )}
            </FlexWrapper>

            <FlexWrapper justifyContent={JustifyContent.SPACE_BETWEEN} fillWidth>
              <Button
                label="Back"
                icon={ArrowLeftIcon}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                onClick={() => setStep(0)}
              />
              <Button
                label="Next"
                icon={ArrowRightIcon}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.SECONDARY}
                onClick={() => setStep(2)}
                isDisabled={isComputingSignature || !signature}
                isIconTrailing
              />
            </FlexWrapper>
          </FlexWrapper>
        )}

        {/* Step 2: Submit */}
        {step === 2 && (
          <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
            <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
              <Text weight={TextWeight.MEDIUM}>Send the request</Text>
              <Text variant={TextVariant.TERTIARY}>
                Copy the command below and run it in your terminal. We&apos;ll confirm receipt
                automatically.
              </Text>
            </FlexWrapper>

            <FlexWrapper alignItems={AlignItems.START} gap={8} fillWidth>
              <CodeEditor
                content={curlCommand}
                lang="bash"
                isReadOnly
                noLineNumbers
                borderRadius={8}
              />
              <Button
                icon={copied ? CheckIcon : CopyIcon}
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.MEDIUM}
                onClick={handleCopy}
              />
            </FlexWrapper>

            <Button
              label="Back"
              icon={ArrowLeftIcon}
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.TERTIARY}
              onClick={() => setStep(1)}
            />
          </FlexWrapper>
        )}
      </FlexWrapper>
    </Widget>
  );
};

export default WebhookStepByStep;
